/**
 * Stock counts: open a sheet, enter what is on the shelf, submit, approve, apply.
 *
 * Only `apply` touches stock, and it writes a **delta** per varying line rather than the counted
 * absolute. That is why applying twice is refused by the server rather than made idempotent: a
 * second delta would move the stock the same distance again and turn a correction into twice the
 * error it was fixing. Nothing here invalidates stock except `applyStockCount`.
 *
 * Recording a line does **not** invalidate the count's own tag. The expected quantities are
 * snapshotted when the count opens and never recomputed, so the only thing a keystroke changes is
 * the line the operator is typing into — refetching the sheet mid-count would fight the person
 * using it, and on a two-hundred-line sheet it would fight them once per line.
 */

import { api } from '../api.js';
import { movementListTag, stockCountListTag, stockCountTag, stockListTag } from '../tags.js';
import type { CategoryId, Paged, ShopId, StockMovement } from '../../types/api.js';
import type {
  StockCount,
  StockCountId,
  StockCountLineId,
  StockCountStatus,
  StockCountSummary,
} from '../../types/stockIntegrity.js';

export type ListStockCountsParams = {
  readonly shopId?: ShopId | undefined;
  readonly status?: StockCountStatus | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type OpenStockCountBody = {
  readonly shopId?: ShopId | undefined;
  /** Absent counts the whole shop. One open count per shop and scope, enforced by the database. */
  readonly categoryId?: CategoryId | undefined;
  readonly note?: string | undefined;
};

export const stockCountsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listStockCounts: build.query<Paged<StockCountSummary>, ListStockCountsParams>({
      query: (params) => ({ url: '/stock-counts', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [stockCountListTag()],
    }),

    getStockCount: build.query<StockCount, StockCountId>({
      query: (id) => `/stock-counts/${id}`,
      providesTags: (_result, _error, id) => [stockCountTag(id)],
    }),

    /**
     * Movements that happened while the count was open, so a variance can be explained rather
     * than merely reported. A sale mid-count is the commonest innocent explanation there is.
     */
    listMovementsDuringCount: build.query<StockMovement[], StockCountId>({
      query: (id) => `/stock-counts/${id}/movements`,
      providesTags: (_result, _error, id) => [stockCountTag(id)],
    }),

    openStockCount: build.mutation<StockCountId, OpenStockCountBody>({
      query: (body) => ({ url: '/stock-counts', method: 'POST', body }),
      invalidatesTags: [stockCountListTag()],
    }),

    /** `qtyCounted: null` clears the line back to "not counted", which is not counting zero. */
    recordCountLine: build.mutation<
      void,
      { countId: StockCountId; lineId: StockCountLineId; qtyCounted: number | null; note?: string | undefined }
    >({
      query: ({ countId, lineId, qtyCounted, note }) => ({
        url: `/stock-counts/${countId}/lines/${lineId}`,
        method: 'PUT',
        body: { qtyCounted, note },
      }),
      // Deliberately no invalidation — see the note at the top of this file.
    }),

    submitStockCount: build.mutation<void, StockCountId>({
      query: (id) => ({ url: `/stock-counts/${id}/submission`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [stockCountTag(id), stockCountListTag()],
    }),

    /** Rejecting returns the count to open for a recount, with its snapshot intact. */
    approveStockCount: build.mutation<
      void,
      { id: StockCountId; approve: boolean; note?: string | undefined }
    >({
      query: ({ id, approve, note }) => ({
        url: `/stock-counts/${id}/approval`,
        method: 'POST',
        body: { approve, note },
      }),
      invalidatesTags: (_result, _error, { id }) => [stockCountTag(id), stockCountListTag()],
    }),

    /** The only call here that writes to the ledger. Approved counts only, and only once. */
    applyStockCount: build.mutation<void, StockCountId>({
      query: (id) => ({ url: `/stock-counts/${id}/application`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        stockCountTag(id),
        stockCountListTag(),
        stockListTag(),
        movementListTag(),
      ],
    }),
  }),
});

export const {
  useListStockCountsQuery,
  useGetStockCountQuery,
  useListMovementsDuringCountQuery,
  useOpenStockCountMutation,
  useRecordCountLineMutation,
  useSubmitStockCountMutation,
  useApproveStockCountMutation,
  useApplyStockCountMutation,
} = stockCountsApi;
